import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ScoreSection from './results/ScoreSection';
import SkillsSection from './results/SkillsSection';
import ExperienceSection from './results/ExperienceSection';
import EducationSection from './results/EducationSection';
import CertificationsSection from './results/CertificationsSection';
import LanguagesSection from './results/LanguagesSection';
import RecommendationsSection from './results/RecommendationsSection';
import CoverLetterGenerator from './CoverLetterGenerator';
import ScoreShareModal from './ScoreShareModal';
import CVOptimizationSuggestions from './CVOptimizationSuggestions';
import CompetitorAnalysis from './CompetitorAnalysis';
import CVTemplateGenerator from './CVTemplateGenerator';
import type { FinalResults } from '../types';
import Logo from '../assests/favicon-logo.png'
import { AlertCircle } from 'lucide-react'; 

interface AnalysisResultsProps {
  results: FinalResults;
  outputId : string
}

const AnalysisResults: React.FC<AnalysisResultsProps> = ({ results,outputId }) => {
  const [showCoverLetterGenerator, setShowCoverLetterGenerator] = useState(false);
  const [showScoreShareModal, setShowScoreShareModal] = useState(false);
  const [showOptimizationSuggestions, setShowOptimizationSuggestions] = useState(false);
  const [showCompetitorAnalysis, setShowCompetitorAnalysis] = useState(false);
  const [showCVTemplateGenerator, setShowCVTemplateGenerator] = useState(false);
  const [showInsufficientMatchModal, setShowInsufficientMatchModal] = useState(false);
  const recommendationsRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  
  useEffect(() => {
    const isAnyModalOpen = showScoreShareModal || showInsufficientMatchModal || showCoverLetterGenerator;
    if (isAnyModalOpen) {
      // Store current scroll position
      const scrollY = window.scrollY;
      
      // Disable scrolling and preserve position
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      // Re-enable scrolling and restore position
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    // Cleanup function to ensure styles are reset
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [showScoreShareModal, showInsufficientMatchModal, showCoverLetterGenerator]);


  return (
    <div className="mt-10 p-2 md:p-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 animate-fadeIn">
      <ScoreSection 
        globalScore={results?.globalScore} 
        scoreBreakdown={results?.scoreBreakdown} 
      />
      
      {/* Main action buttons */}
      {/* <div className="mt-8 mb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"> */}
         {/* Cover letter generator button */}
         <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 mt-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
            ✉️ <span className="ml-2">{t('results.cover_letter.title')}</span>
          </h3>
          <p className="text-gray-600 mb-4">
            {t('results.cover_letter.description')}
          </p>
          <button
            onClick={() => {
              if ((results?.globalScore ?? 0) < 30) {
                setShowInsufficientMatchModal(true);
              } else {
                setShowCoverLetterGenerator(true);
              }
            }}
            className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all transform hover:-translate-y-1 flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {t('results.cover_letter.button')}
          </button>
        </div>
        
        
        {/* Share score button */}
        <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 mt-8">
            <div className="flex gap-x-2">
              <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
              </h3>
              <img src={Logo} className='max-h-8 max-w-8 min-h-8 min-w-8 rounded-full'/>
              <span className="ml-2">{t('results.share_score.title')}</span>
            </div>
          <p className="text-gray-600 mb-4">
            {t('results.share_score.description', { score: results?.globalScore })}
          </p>
          <button
            onClick={() => setShowScoreShareModal(true)}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all transform hover:-translate-y-1 flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
            </svg>
            {t('results.share_score.button', { score: results?.globalScore })}
          </button>
        </div> 

       

        {/* Optimized CV button */}
        {/* <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
            📄 <span className="ml-2">{t('results.optimized_cv.title')}</span>
          </h3>
          <p className="text-gray-600 mb-4">
            {t('results.optimized_cv.description')}
          </p>
          <button
            onClick={() => setShowCVTemplateGenerator(true)}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all transform hover:-translate-y-1 flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {t('results.optimized_cv.button')}
          </button>
        </div>
      </div> */}

      {/* Advanced analysis buttons */}
      {/* <div className="mb-8 mt-8 grid grid-cols-1 md:grid-cols-2 gap-4"> */}
        {/* Optimization suggestions */}
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-200 mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            💡 <span className="ml-2">{t('results.optimization.title')}</span>
          </h3>
          <p className="text-gray-600 mb-4 text-sm">
            {t('results.optimization.description')}
          </p>
          <button
            onClick={() => {
              // Scroll to recommendations section
              setTimeout(() => {
                recommendationsRef.current?.scrollIntoView({ 
                  behavior: 'smooth',
                  block: 'start'
                });
              }, 100);
            }}
            className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-2 rounded-lg font-medium hover:shadow-lg transition-all transform hover:-translate-y-1 flex items-center justify-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            {t('results.optimization.button')}
          </button>
        </div>

        {/* Competitor analysis */}
        {/* <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            📊 <span className="ml-2">{t('results.competitor_analysis.title')}</span>
          </h3>
          <p className="text-gray-600 mb-4 text-sm">
            {t('results.competitor_analysis.description')}
          </p>
          <button
            onClick={() => setShowCompetitorAnalysis(true)}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-4 py-2 rounded-lg font-medium hover:shadow-lg transition-all transform hover:-translate-y-1 flex items-center justify-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            {t('results.competitor_analysis.button')}
          </button>
        </div> */}
      {/* </div> */}
      
      <div className="mt-8 space-y-6">
        {results?.analysisResults?.skills && (
          <SkillsSection data={results?.analysisResults?.skills} />
        )}
        
        {results?.analysisResults?.experience && (
          <ExperienceSection data={results?.analysisResults?.experience} />
        )}
        
        {results?.analysisResults?.education && (
          <EducationSection data={results?.analysisResults?.education} />
        )}
        
        {results?.analysisResults?.certifications && (
          <CertificationsSection data={results?.analysisResults?.certifications} />
        )}
        
        {results?.analysisResults?.languages && (
          <LanguagesSection data={results?.analysisResults?.languages} />
        )}
        
        {results?.recommendations?.length > 0 && (
          <div ref={recommendationsRef}>
            <RecommendationsSection recommendations={results?.recommendations} />
          </div>
        )}
      </div>

      {/* Modals */}
      {showCoverLetterGenerator && (
        <CoverLetterGenerator
          analysisResults={results}
          onClose={() => setShowCoverLetterGenerator(false)}
          outputId={outputId}
        />
      )}

      {showScoreShareModal && (
        <ScoreShareModal
          isOpen={showScoreShareModal}
          onClose={() => setShowScoreShareModal(false)}
          results={results}
        />
      )}

      {showOptimizationSuggestions && (
        <CVOptimizationSuggestions
          results={results}
          isOpen={showOptimizationSuggestions}
          onClose={() => setShowOptimizationSuggestions(false)}
        />
      )}

      {showCompetitorAnalysis && (
        <CompetitorAnalysis
          results={results}
          isOpen={showCompetitorAnalysis}
          onClose={() => setShowCompetitorAnalysis(false)}
        />
      )}

      {showCVTemplateGenerator && (
        <CVTemplateGenerator
          results={results}
          isOpen={showCVTemplateGenerator}
          onClose={() => setShowCVTemplateGenerator(false)}
        />
      )}

      {/* Insufficient Match Modal */}
      {showInsufficientMatchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4">
            <div className="p-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t('cover_letter.insufficient_match.title')}
              </h3>
              <p className="text-gray-600 mb-6">
                {t('cover_letter.insufficient_match.description')}
              </p>
              <div className="flex justify-center">
                <button
                  onClick={() => setShowInsufficientMatchModal(false)}
                  className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
                >
                  {t('cover_letter.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisResults;